"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import FormSelect from "@/components/FormSelect";
import type { CountryTimezoneGroup } from "@/services/countryTimezone";

/**
 * Sentinel values for the in-place drill-in, in the same style as the free-
 * trial category select's "Other" group. Real values are IANA zones ("Area/
 * City"), which never start with "__", so these cannot collide. Neither is
 * ever submitted.
 */
const COUNTRY_VALUE_PREFIX = "__country__:";
const BACK_VALUE = "__back__";

type Props = {
  label: string;
  placeholder: string;
  groups: CountryTimezoneGroup[];
  /** The selected IANA zone, or "". */
  value: string;
  onChange: (timezone: string) => void;
  error?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
};

/**
 * "Country / Time Zone" as one control: every country is listed once, and the
 * value it produces is always a time zone. A country with a single zone is
 * picked directly; one with several (the US, Canada, Russia, Brazil...) opens
 * its zones as a sub-menu in the same dropdown, with a way back to the list.
 */
export default function CountryTimezoneSelect({
  label,
  placeholder,
  groups,
  value,
  onChange,
  error,
  isLoading,
  isDisabled,
}: Props) {
  const t = useTranslations("auth.profileStep");
  const [menuOpen, setMenuOpen] = useState(false);
  const [openedCountry, setOpenedCountry] = useState<string | null>(null);

  const selectedGroup = useMemo(
    () => groups.find((group) => group.zones.some((zone) => zone.value === value)) ?? null,
    [groups, value],
  );

  // The sub-menu the user opened, or - so the selected value always has an
  // option to display - the one the current selection lives in.
  const viewCode =
    openedCountry ?? (selectedGroup && selectedGroup.zones.length > 1 ? selectedGroup.code : null);
  const viewGroup = viewCode ? (groups.find((group) => group.code === viewCode) ?? null) : null;

  const options = viewGroup
    ? [
        { value: BACK_VALUE, label: t("country_timezone_back") },
        ...viewGroup.zones.map((zone) => ({
          value: zone.value,
          label: `${zone.place} ${zone.offset}`.trim(),
        })),
      ]
    : groups.map((group) =>
        group.zones.length === 1
          ? { value: group.zones[0].value, label: `${group.name} ${group.zones[0].offset}`.trim() }
          : // "›" is bidi-mirrored, so it points the right way under dir="rtl"
            // on its own - same as the category select's "Other ›".
            { value: `${COUNTRY_VALUE_PREFIX}${group.code}`, label: `${group.name} ›` },
      );

  return (
    <FormSelect
      label={label}
      placeholder={
        viewGroup ? t("select_timezone_in_country", { country: viewGroup.name }) : placeholder
      }
      options={options}
      value={value}
      error={error}
      isLoading={isLoading}
      isDisabled={isDisabled}
      // Controlled menu + closeMenuOnSelect=false so opening a country (or
      // going back) keeps the dropdown open with the swapped list; only
      // picking a zone closes it.
      closeMenuOnSelect={false}
      menuIsOpen={menuOpen}
      onMenuOpen={() => setMenuOpen(true)}
      onMenuClose={() => setMenuOpen(false)}
      // In a sub-menu the zone alone is enough; once it is the selected value
      // it needs its country beside it.
      formatOptionLabel={(option, meta) =>
        meta.context === "value" && viewGroup && option.value !== BACK_VALUE
          ? `${viewGroup.name} — ${option.label}`
          : option.label
      }
      onChange={(next) => {
        if (next.startsWith(COUNTRY_VALUE_PREFIX)) {
          setOpenedCountry(next.slice(COUNTRY_VALUE_PREFIX.length));
          setMenuOpen(true);
          return;
        }

        if (next === BACK_VALUE) {
          // Back to the country list, still open. A selection inside this
          // country is cleared, or it would pull the view straight back here.
          setOpenedCountry(null);
          if (selectedGroup?.code === viewCode) {
            onChange("");
          }
          setMenuOpen(true);
          return;
        }

        setOpenedCountry(null);
        onChange(next);
        setMenuOpen(false);
      }}
    />
  );
}
